import { ref, computed, watch } from '@vue/composition-api'
import store from '@/store'
import useCommon from '@/views/organization/useCommon'
import { useLambda } from '@/views/habit/awsLambda'
import { oid } from '@/helpers/mongoHelpers'

export default function useIndicators() {
  const { handleError } = useCommon()
  const { getItemsAggregate } = useLambda()
  const { client } = store.state?.userStore?.userData

  const companyFilter = computed(() => store.state.filtersStore.companyFilter)
  const idFilter = computed(() => store.state.filtersStore.idFilter)

  const isLoading = ref(true)
  const treatments = ref([])
  const aris = ref([])
  const arrs = ref([])
  const gaps = ref([])
  const arsopbl = ref([])

  // KPIs de Gestión
  const kpisGestion = computed(() => {
    const t = treatments.value
    const a = aris.value
    const r = arrs.value
    const g = gaps.value

    const totalTreatments = t.length
    const totalARIs = a.length
    const totalARRs = r.length

    // KPI 2: % ARI completo
    const ariCompletedPct = totalTreatments > 0 ? Math.round((totalARIs / totalTreatments) * 100) : 0

    // KPI 3: Tratamientos con EIPD
    const eipdCount = a.filter(x => x.eipdFinal === true).length

    // KPI 4: % Riesgo alto/crítico
    const highCritical = a.filter(x => {
      const level = x.inherentLevel
      return level === 'Alto' || level === 'Muy Alto' || level === 'Crítico'
    }).length
    const highRiskPct = totalARIs > 0 ? Math.round((highCritical / totalARIs) * 100) : 0

    // KPI 5: Riesgo promedio inherente
    const inherentRisks = a.map(x => x.inherentRisk).filter(x => x != null && !isNaN(x))
    const avgInherent = inherentRisks.length > 0 ? Math.round((inherentRisks.reduce((s, v) => s + v, 0) / inherentRisks.length) * 10) / 10 : 0

    // KPI 6: Riesgo promedio residual
    const residualRisks = r.map(x => x.residualRisk).filter(x => x != null && !isNaN(x))
    const avgResidual = residualRisks.length > 0 ? Math.round((residualRisks.reduce((s, v) => s + v, 0) / residualRisks.length) * 10) / 10 : 0

    // KPI 7: % Reducción de riesgo
    const riskReduction = avgInherent > 0 ? Math.round(((avgInherent - avgResidual) / avgInherent) * 100) : 0

    // KPI 8: Transferencias internacionales
    const transferCount = t.filter(x => x.transferencias?.destinatario && x.transferencias.destinatario !== '').length

    // KPI 9: Tratamientos con sensibles
    const sensitiveCount = t.filter(x => x.hasSensitive === true).length

    // KPI 10: Tratamientos con NNA
    const minorsCount = t.filter(x => x.hasMinors === true).length

    // KPI 11: % Cumplimiento GAP
    const nonNAGaps = g.filter(x => x.status !== 'No aplica')
    const implementedGaps = nonNAGaps.filter(x => x.status === 'Implementado')
    const gapCompliance = nonNAGaps.length > 0 ? Math.round((implementedGaps.length / nonNAGaps.length) * 100) : 0

    // KPI 12: Cumplimiento GAP — Sin brechas (% obligaciones que NO están 'No implementado', incluyendo 'No aplica')
    const allGaps = g.length
    const noBreachGaps = g.filter(x => x.status !== 'No implementado')
    const noBreachPct = allGaps > 0 ? Math.round((noBreachGaps.length / allGaps) * 100) : 0

    // KPI 13: Planes de acción cerrados
    const gapsWithPlan = g.filter(x => x.status === 'Parcial' || x.status === 'No implementado')
    const closedPlans = gapsWithPlan.filter(x => x.planStatus === 'Cerrado').length
    const closedPlansPct = gapsWithPlan.length > 0 ? Math.round((closedPlans / gapsWithPlan.length) * 100) : 0

    // Cobertura ARR (se mantiene para posible uso futuro)
    // const treatmentIdsWithARR = new Set(r.map(x => x.treatment_id))
    // const arrCoverage = totalARIs > 0 ? Math.round((treatmentIdsWithARR.size / totalARIs) * 100) : 0

    return [
      { key: 'rat_coverage', title: 'Cobertura RAT', value: totalTreatments, unit: '', target: null, status: 'info' },
      { key: 'eipd_required', title: 'Tratamientos con EIPD', value: eipdCount, unit: '', target: null, status: 'info' },
      { key: 'avg_inherent', title: 'Riesgo promedio inherente', value: avgInherent, unit: '', target: null, status: 'info' },
      { key: 'avg_residual', title: 'Riesgo promedio residual', value: avgResidual, unit: '', target: null, status: 'info' },
      { key: 'transfers', title: 'Transferencias internacionales', value: transferCount, unit: '', target: null, status: 'info' },
      { key: 'sensitive', title: 'Tratamientos con sensibles', value: sensitiveCount, unit: '', target: null, status: 'info' },
      { key: 'minors', title: 'Tratamientos con NNA', value: minorsCount, unit: '', target: null, status: 'info' },
    ]
  })

  // Indicadores de Objetivo (porcentajes con semaforización)
  const kpisObjetivo = computed(() => {
    const t = treatments.value
    const a = aris.value
    const g = gaps.value

    const totalTreatments = t.length
    const totalARIs = a.length

    const ariCompletedPct = totalTreatments > 0 ? Math.round((totalARIs / totalTreatments) * 100) : 0

    const highCritical = a.filter(x => {
      const level = x.inherentLevel
      return level === 'Alto' || level === 'Muy Alto' || level === 'Crítico'
    }).length
    const highRiskPct = totalARIs > 0 ? Math.round((highCritical / totalARIs) * 100) : 0

    const inherentRisks = a.map(x => x.inherentRisk).filter(x => x != null && !isNaN(x))
    const avgInherent = inherentRisks.length > 0 ? Math.round((inherentRisks.reduce((s, v) => s + v, 0) / inherentRisks.length) * 10) / 10 : 0

    const residualRisks = arrs.value.map(x => x.residualRisk).filter(x => x != null && !isNaN(x))
    const avgResidual = residualRisks.length > 0 ? Math.round((residualRisks.reduce((s, v) => s + v, 0) / residualRisks.length) * 10) / 10 : 0

    const riskReduction = avgInherent > 0 ? Math.round(((avgInherent - avgResidual) / avgInherent) * 100) : 0

    const nonNAGaps = g.filter(x => x.status !== 'No aplica')
    const implementedGaps = nonNAGaps.filter(x => x.status === 'Implementado')
    const gapCompliance = nonNAGaps.length > 0 ? Math.round((implementedGaps.length / nonNAGaps.length) * 100) : 0

    const allGaps = g.length
    const noBreachGaps = g.filter(x => x.status !== 'No implementado')
    const noBreachPct = allGaps > 0 ? Math.round((noBreachGaps.length / allGaps) * 100) : 0

    const gapsWithPlan = g.filter(x => x.status === 'Parcial' || x.status === 'No implementado')
    const closedPlans = gapsWithPlan.filter(x => x.planStatus === 'Cerrado').length
    const closedPlansPct = gapsWithPlan.length > 0 ? Math.round((closedPlans / gapsWithPlan.length) * 100) : 0

    return [
      { key: 'ari_completed', title: '% ARI completo', value: ariCompletedPct, unit: '%', target: 80, status: getThreshold(ariCompletedPct, 80, 50) },
      { key: 'high_risk_pct', title: '% Riesgo alto/crítico', value: highRiskPct, unit: '%', target: 20, status: getThresholdInverse(highRiskPct, 20, 40) },
      { key: 'risk_reduction', title: '% Reducción de riesgo', value: riskReduction, unit: '%', target: 50, status: getThreshold(riskReduction, 50, 25) },
      { key: 'gap_compliance', title: '% Cumplimiento GAP - Implementado', value: gapCompliance, unit: '%', target: 80, status: getThreshold(gapCompliance, 80, 50) },
      { key: 'no_breach', title: '% Cumplimiento GAP — Sin brechas', value: noBreachPct, unit: '%', target: 80, status: getThreshold(noBreachPct, 80, 50) },
      { key: 'closed_plans', title: 'Planes de acción cerrados', value: closedPlansPct, unit: '%', target: 70, status: getThreshold(closedPlansPct, 70, 40) },
    ]
  })

  // Indicadores de Resultados
  const kpisResultados = computed(() => {
    const t = treatments.value
    const a = aris.value
    const r = arrs.value
    const g = gaps.value

    const eipdCount = a.filter(x => x.eipdFinal === true).length
    const planesVencidos = g.filter(x =>
      (x.status === 'Parcial' || x.status === 'No implementado') &&
      x.planStatus !== 'Cerrado' &&
      x.deadline && new Date(x.deadline) < new Date()
    ).length
    const gapAbiertas = g.filter(x => x.status === 'Parcial' || x.status === 'No implementado').length

    return [
      { key: 'total_treatments', title: 'Total tratamientos registrados', value: t.length },
      { key: 'treatments_active', title: 'Tratamientos activos', value: t.filter(x => x.status !== 'draft').length },
      { key: 'treatments_draft', title: 'Tratamientos en borrador', value: t.filter(x => x.status === 'draft').length },
      { key: 'total_ari', title: 'Total ARI completadas', value: a.length },
      { key: 'ari_eipd', title: 'ARI con EIPD requerida', value: eipdCount },
      { key: 'total_arr', title: 'Total ARR con plan activo', value: r.filter(x => x.status !== 'Cerrado').length },
      { key: 'planes_vencidos', title: 'Planes vencidos', value: planesVencidos },
      { key: 'gap_abiertas', title: 'Brechas GAP abiertas', value: gapAbiertas },
    ]
  })

  // Datos para gráficos
  const donutRiskData = computed(() => {
    const levels = { 'Bajo': 0, 'Medio': 0, 'Alto': 0, 'Muy Alto': 0, 'Crítico': 0 }
    const levelMap = { low: 'Bajo', medium: 'Medio', high: 'Alto', veryHigh: 'Muy Alto', critical: 'Crítico' }

    for (const ari of aris.value) {
      const level = levelMap[ari.inherentLevel] || ari.inherentLevel
      if (levels[level] !== undefined) levels[level]++
    }

    return {
      labels: Object.keys(levels),
      values: Object.values(levels),
      colors: ['#70AD47', '#FFC000', '#ED7D31', '#C00000', '#823535'],
    }
  })

  const barPlanStatusData = computed(() => {
    const statuses = { 'Pendiente': 0, 'En progreso': 0, 'Cerrado': 0 }

    for (const arr of arrs.value) {
      const ps = arr.status || 'Pendiente'
      if (statuses[ps] !== undefined) statuses[ps]++
    }

    return {
      labels: Object.keys(statuses),
      values: Object.values(statuses),
      colors: ['#ff9f43', '#00cfe8', '#28c76f'],
    }
  })

  const barGapComplianceData = computed(() => {
    const obligations = {}
    for (const gap of gaps.value) {
      const name = gap.obligationName || gap.obligation?.name || 'Sin obligación'
      if (!obligations[name]) obligations[name] = { Implementado: 0, Parcial: 0, 'No implementado': 0, 'No aplica': 0 }
      const status = gap.status || 'No implementado'
      if (obligations[name][status] !== undefined) obligations[name][status]++
    }

    return {
      labels: Object.keys(obligations),
      data: obligations,
    }
  })

  // ── Indicadores ARSOPB+L ──────────────────────────────────────────────

  const RIGHT_LABELS = {
    ACC: 'Acceso',
    RECT: 'Rectificación',
    SUPR: 'Supresión',
    OPO: 'Oposición',
    PORT: 'Portabilidad',
    BLOQ: 'Bloqueo',
    LIMI: 'Limitación',
  }

  // Tiempo de respuesta en días hábiles para solicitudes cerradas (resueltas/rechazadas)
  const closedRequests = computed(() => arsopbl.value.filter(x => x.closedAt && (x.status === 'resolved' || x.status === 'rejected')))

  const businessDaysResponse = (request) => {
    const start = request.createdAt?.$date || request.createdAt
    const end = request.closedAt?.$date || request.closedAt
    if (!start || !end) return null
    const s = new Date(start)
    const e = new Date(end)
    if (isNaN(s.getTime()) || isNaN(e.getTime())) return null
    s.setHours(0, 0, 0, 0)
    e.setHours(0, 0, 0, 0)
    let count = 0
    const current = new Date(s)
    while (current < e) {
      current.setDate(current.getDate() + 1)
      const day = current.getDay()
      if (day !== 0 && day !== 6) count++
    }
    return count
  }

  // 1) Solicitudes por mes (últimos 12 meses)
  const arsopblByMonth = computed(() => {
    const now = new Date()
    const buckets = []
    const monthNames = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic']
    for (let i = 11; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
      buckets.push({ key: `${d.getFullYear()}-${d.getMonth()}`, label: `${monthNames[d.getMonth()]} ${String(d.getFullYear()).slice(2)}`, count: 0 })
    }
    const bucketMap = {}
    buckets.forEach(b => { bucketMap[b.key] = b })

    for (const req of arsopbl.value) {
      const raw = req.createdAt?.$date || req.createdAt
      const d = new Date(raw)
      if (isNaN(d.getTime())) continue
      const key = `${d.getFullYear()}-${d.getMonth()}`
      if (bucketMap[key]) bucketMap[key].count++
    }

    return {
      labels: buckets.map(b => b.label),
      values: buckets.map(b => b.count),
    }
  })

  // 2) Tiempo promedio de respuesta (días hábiles)
  const arsopblAvgResponse = computed(() => {
    const times = closedRequests.value.map(businessDaysResponse).filter(v => v != null)
    if (!times.length) return 0
    return Math.round((times.reduce((s, v) => s + v, 0) / times.length) * 10) / 10
  })

  // 3) % dentro de plazo (cerradas en <= 20 días hábiles)
  const arsopblOnTimePct = computed(() => {
    const closed = closedRequests.value
    if (!closed.length) return 0
    const onTime = closed.filter(x => {
      const days = businessDaysResponse(x)
      return days != null && days <= 20
    }).length
    return Math.round((onTime / closed.length) * 100)
  })

  // 4) Distribución por derecho
  const arsopblByRight = computed(() => {
    const counts = {}
    Object.keys(RIGHT_LABELS).forEach(code => { counts[code] = 0 })
    for (const req of arsopbl.value) {
      if (counts[req.rightCode] !== undefined) counts[req.rightCode]++
    }
    const entries = Object.keys(RIGHT_LABELS).filter(code => counts[code] > 0)
    return {
      labels: entries.map(code => RIGHT_LABELS[code]),
      values: entries.map(code => counts[code]),
      colors: ['#498ceb', '#00cfe8', '#28c76f', '#ff9f43', '#ea5455', '#7367f0', '#82868b'],
    }
  })

  // KPIs resumen ARSOPB+L (tarjetas)
  const arsopblKpis = computed(() => [
    { key: 'arsopbl_total', title: 'Total solicitudes', value: arsopbl.value.length, unit: '' },
    { key: 'arsopbl_avg_response', title: 'Tiempo promedio de respuesta', value: arsopblAvgResponse.value, unit: ' días' },
    { key: 'arsopbl_on_time', title: '% dentro de plazo', value: arsopblOnTimePct.value, unit: '%' },
  ])

  const levelMap = { low: 'Bajo', medium: 'Medio', high: 'Alto', veryHigh: 'Muy Alto', critical: 'Crítico' }

  // Drill-down data
  const getDrilldownData = (kpiKey) => {
    const ariTreatmentIds = new Set(aris.value.map(x => x.treatment_id))
    const arrTreatmentIds = new Set(arrs.value.map(x => x.treatment_id))

    switch (kpiKey) {
      case 'rat_coverage':
        return treatments.value.map(t => ({
          _id: t._id, treatmentId: t.treatmentId, nombre: t.nombre, route: 'privacy-treatment-edit',
        }))

      case 'ari_completed':
        return treatments.value.map(t => ({
          _id: t._id, treatmentId: t.treatmentId, nombre: t.nombre,
          ariStatus: ariTreatmentIds.has(t._id) ? 'Sí' : 'No',
          route: 'privacy-treatment-edit',
        }))

      case 'eipd_required':
        return aris.value.filter(x => x.eipdFinal === true).map(x => ({
          _id: x._id, treatmentId: x.treatmentId, nombre: x.treatmentName,
          route: 'privacy-inherent-risk-edit',
        }))

      case 'high_risk_pct':
        return aris.value.map(x => ({
          _id: x._id, treatmentId: x.treatmentId, nombre: x.treatmentName,
          level: levelMap[x.inherentLevel] || x.inherentLevel, risk: x.inherentRisk,
          route: 'privacy-inherent-risk-edit',
        }))

      case 'avg_inherent':
        return aris.value.map(x => ({
          _id: x._id, treatmentId: x.treatmentId, nombre: x.treatmentName,
          level: levelMap[x.inherentLevel] || x.inherentLevel, risk: x.inherentRisk,
          route: 'privacy-inherent-risk-edit',
        }))

      case 'avg_residual':
        return arrs.value.map(x => ({
          _id: x._id, treatmentId: x.treatmentId, nombre: x.treatmentName,
          level: levelMap[x.residualLevel] || x.residualLevel, risk: x.residualRisk,
          route: 'privacy-residual-risk-edit',
        }))

      case 'risk_reduction':
        return aris.value.filter(x => arrTreatmentIds.has(x.treatment_id)).map(x => {
          const arr = arrs.value.find(r => r.treatment_id === x.treatment_id)
          return {
            _id: x._id, treatmentId: x.treatmentId, nombre: x.treatmentName,
            inherentRisk: x.inherentRisk, residualRisk: arr?.residualRisk || '-',
            route: 'privacy-inherent-risk-edit',
          }
        })

      case 'transfers':
        return treatments.value.filter(x => x.transferencias?.destinatario && x.transferencias.destinatario !== '').map(t => ({
          _id: t._id, treatmentId: t.treatmentId, nombre: t.nombre,
          destinatario: t.transferencias.destinatario,
          route: 'privacy-treatment-edit',
        }))

      case 'sensitive':
        return treatments.value.filter(x => x.hasSensitive === true).map(t => ({
          _id: t._id, treatmentId: t.treatmentId, nombre: t.nombre,
          route: 'privacy-treatment-edit',
        }))

      case 'minors':
        return treatments.value.filter(x => x.hasMinors === true).map(t => ({
          _id: t._id, treatmentId: t.treatmentId, nombre: t.nombre,
          route: 'privacy-treatment-edit',
        }))

      case 'gap_compliance':
        return gaps.value.filter(x => x.status !== 'No aplica').map(x => ({
          _id: x._id, nombre: x.obligationName, status: x.status, planStatus: x.planStatus || '-',
          route: 'privacy-gap-edit',
        }))

      case 'no_breach':
        return gaps.value.map(x => ({
          _id: x._id, nombre: x.obligationName, status: x.status, planStatus: x.planStatus || '-',
          route: 'privacy-gap-edit',
        }))

      case 'closed_plans':
        return gaps.value.filter(x => x.status === 'Parcial' || x.status === 'No implementado').map(x => ({
          _id: x._id, nombre: x.obligationName, status: x.status,
          planStatus: x.planStatus || 'Pendiente', deadline: x.deadline || '-',
          route: 'privacy-gap-edit',
        }))

      default:
        return []
    }
  }

  // Thresholds
  function getThreshold(value, okMin, warningMin) {
    if (value >= okMin) return 'ok'
    if (value >= warningMin) return 'warning'
    return 'critical'
  }

  function getThresholdInverse(value, okMax, warningMax) {
    if (value <= okMax) return 'ok'
    if (value <= warningMax) return 'warning'
    return 'critical'
  }

  // Fetch data
  const fetchData = async () => {
    isLoading.value = true

    try {
      const baseQuery = { client_id: oid(client.$oid), deleted: { $ne: true } }

      const conditions = []
      if (companyFilter.value.length > 0) {
        conditions.push({ empresa_id: { $in: companyFilter.value.map(e => oid(e)) } })
      }

      const treatmentQuery = { ...baseQuery }
      if (conditions.length) treatmentQuery.$and = conditions

      // Fetch treatments
      const treatmentPipeline = [
        { $match: treatmentQuery },
        { $sort: { treatmentId: -1 } },
        { $project: { treatmentId: 1, nombre: 1, status: 1, hasSensitive: 1, hasMinors: 1, hasBiometric: 1, transferencias: 1, empresa_id: 1 } },
      ]

      // Fetch ARIs
      const ariQuery = { ...baseQuery }
      const ariPipeline = [
        { $match: ariQuery },
        {
          $lookup: {
            from: 'treatment', localField: 'treatment_id', foreignField: '_id', as: 'treatment',
            pipeline: [{ $project: { treatmentId: 1, nombre: 1, empresa_id: 1 } }]
          }
        },
        { $addFields: { treatment: { $arrayElemAt: ['$treatment', 0] }, treatmentId: { $arrayElemAt: ['$treatment.treatmentId', 0] }, treatmentName: { $arrayElemAt: ['$treatment.nombre', 0] } } },
      ]

      if (companyFilter.value.length > 0) {
        ariPipeline.push({ $match: { 'treatment.empresa_id': { $in: companyFilter.value.map(e => oid(e)) } } })
      }
      if (idFilter.value.length > 0) {
        ariPipeline.push({ $match: { 'treatment._id': { $in: idFilter.value.map(e => oid(e)) } } })
      }

      ariPipeline.push({ $sort: { treatmentId: -1 } })
      ariPipeline.push({ $project: { treatment_id: 1, calculatedP: 1, calculatedI: 1, inherentRisk: 1, inherentLevel: 1, eipdFinal: 1, treatmentId: 1, treatmentName: 1 } })

      // Fetch ARRs
      const arrQuery = { ...baseQuery }
      const arrPipeline = [
        { $match: arrQuery },
        {
          $lookup: {
            from: 'treatment', localField: 'treatment_id', foreignField: '_id', as: 'treatment',
            pipeline: [{ $project: { treatmentId: 1, nombre: 1, empresa_id: 1 } }]
          }
        },
        { $addFields: { treatment: { $arrayElemAt: ['$treatment', 0] }, treatmentId: { $arrayElemAt: ['$treatment.treatmentId', 0] }, treatmentName: { $arrayElemAt: ['$treatment.nombre', 0] } } },
      ]

      if (companyFilter.value.length > 0) {
        arrPipeline.push({ $match: { 'treatment.empresa_id': { $in: companyFilter.value.map(e => oid(e)) } } })
      }

      arrPipeline.push({ $sort: { treatmentId: -1 } })
      arrPipeline.push({ $project: { treatment_id: 1, residualP: 1, residualI: 1, residualRisk: 1, residualLevel: 1, status: 1, treatmentId: 1, treatmentName: 1 } })

      // Fetch GAPs
      const gapQuery = { ...baseQuery }
      const gapPipeline = [
        { $match: gapQuery },
        {
          $lookup: {
            from: 'gapObligation', localField: 'obligation_id', foreignField: '_id', as: 'obligation',
            pipeline: [{ $project: { name: 1, order: 1 } }]
          }
        },
        { $addFields: { obligation: { $arrayElemAt: ['$obligation', 0] }, obligationName: { $arrayElemAt: ['$obligation.name', 0] } } },
        { $project: { status: 1, planStatus: 1, deadline: 1, obligation_id: 1, obligationName: 1 } },
      ]

      // Fetch ARSOPB+L requests (client-scoped; no soft-delete on this collection)
      const arsopblPipeline = [
        { $match: { client_id: oid(client.$oid) } },
        { $project: { caseNumber: 1, rightCode: 1, titular: 1, stage: 1, status: 1, createdAt: 1, closedAt: 1 } },
      ]

      const [treatmentResult, ariResult, arrResult, gapResult, arsopblResult] = await Promise.all([
        getItemsAggregate({ collection: 'treatment', pipeline: treatmentPipeline }),
        getItemsAggregate({ collection: 'inherentAnalysis', pipeline: ariPipeline }),
        getItemsAggregate({ collection: 'residualAnalysis', pipeline: arrPipeline }),
        getItemsAggregate({ collection: 'gap', pipeline: gapPipeline }),
        getItemsAggregate({ collection: 'arsopbl', pipeline: arsopblPipeline }),
      ])

      treatments.value = treatmentResult || []
      aris.value = ariResult || []
      arrs.value = arrResult || []
      gaps.value = gapResult || []
      arsopbl.value = arsopblResult || []
    } catch (error) {
      console.log(error)
      handleError({ error, defaultMessage: 'Error al cargar indicadores' })
    } finally {
      isLoading.value = false
    }
  }

  watch([companyFilter, idFilter], () => {
    fetchData()
  })

  return {
    isLoading,
    kpisGestion,
    kpisObjetivo,
    kpisResultados,
    donutRiskData,
    barPlanStatusData,
    barGapComplianceData,
    arsopblKpis,
    arsopblByMonth,
    arsopblByRight,
    getDrilldownData,
    fetchData,
  }
}
